/**
 * The admin Feature Flags page's view of a flag description. Every definition
 * served by `GET /admin/feature-flags` carries `on` / `off` / `commercialHome`
 * split from its description (src/types/featureFlags.ts, pinned by
 * featureFlagDefinitions.test.ts). This is the fallback for a definition that
 * lacks them, with the same rules as src/utils/featureFlagDescription.ts:
 * "On: ... Off: ... Commercial home: <home> ...", where a planned flag has no
 * "On:" and its lead-in is the on text.
 */
export interface FlagDescriptionParts {
  on: string;
  off: string;
  commercialHome: string;
  commercialHomeNote?: string;
}

export function parseFlagDescription(description: string): FlagDescriptionParts | null {
  const offIndex = description.indexOf('Off: ');
  const homeIndex = description.indexOf('Commercial home: ');
  if (offIndex < 0 || homeIndex < offIndex) return null;
  const onMatch = /(^|[.)] )On: /.exec(description.slice(0, offIndex));
  const on = onMatch
    ? description.slice(onMatch.index + onMatch[0].length, offIndex).trim()
    : description.slice(0, offIndex).trim();
  const off = description.slice(offIndex + 'Off: '.length, homeIndex).trim();
  const rest = description.slice(homeIndex + 'Commercial home: '.length).trim();
  const homeMatch = /^[A-Za-z]+/.exec(rest);
  if (!on || !off || !homeMatch) return null;
  const commercialHome = homeMatch[0];
  const note = rest
    .slice(commercialHome.length)
    .replace(/^\.\s*/, '')
    .trim();
  return note && note !== '.' ? { on, off, commercialHome, commercialHomeNote: note } : { on, off, commercialHome };
}

/**
 * The template's sentences start lower case ("On: a store gets ..."). Shown on
 * their own under a "When on" label they read better capitalised, but only
 * when the first word is a plain word: `lastPushed` or `/ai/chat` stay as written.
 */
export function sentenceCase(text: string): string {
  return /^[a-z]+(?=[\s,;:.]|$)/.test(text) ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

interface DescribedFlag {
  description: string;
  on?: string;
  off?: string;
  commercialHome?: string;
  commercialHomeNote?: string;
}

/** The structured parts of a definition, falling back to the description split, then to the description itself. */
export function flagDescriptionParts(def: DescribedFlag): FlagDescriptionParts {
  if (def.on && def.off && def.commercialHome) {
    return { on: def.on, off: def.off, commercialHome: def.commercialHome, commercialHomeNote: def.commercialHomeNote };
  }
  return parseFlagDescription(def.description) ?? { on: def.description, off: '', commercialHome: '' };
}
